"use client"

import { useState } from "react"
import { ChevronDown, ChevronLeft, Clock, MoreHorizontal, Plus } from "lucide-react"
import { Link } from "@tanstack/react-router"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { JobEditDialog } from "@/components/job-edit-dialog"
import { useJobHeaderAction } from "./job-header-action"

import type { JobConsoleSections, SectionState } from "@/hooks/use-job-console"
import { cn } from "@/lib/utils"
import type {
  ActivityEntry,
  ActivityTarget,
  ConsoleTask,
  GraphiteJobConsoleData,
  StageCount,
} from "./graphite-job-console-data"

/**
 * The job console, from canvas artboard 4a.
 *
 * The canvas calls this "instrument logic": a readout strip, then open tasks by
 * due date, then the activity log as a three-column ledger — timestamp,
 * primitive, record. The description collapses to a disclosure; spec, managers
 * and searches are a mono key/value sheet.
 *
 * And, in the canvas's own words: "No cards anywhere." The whole console is one
 * surface with hairlines dividing it, not a grid of panels. That is the single
 * biggest departure from how ATS detail pages are usually built, and it is the
 * reason the page reads as an instrument rather than a dashboard.
 *
 * Note the activity ledger's middle column is the *primitive* Robin ran —
 * `search run`, `stage moved` — set in mono because it is machine output, even
 * though it reads as words. That column is the receipt trail for the job.
 */

const LABEL = "text-[11.5px] font-medium text-muted-foreground"

const TASK_TONE = {
  overdue: { dot: "bg-destructive", due: "text-destructive" },
  due: { dot: "bg-[#FB8A3C]", due: "text-[#FB8A3C]" },
  upcoming: { dot: "bg-primary", due: "text-brand-ink" },
} as const

function ReadoutStrip({ figures }: { figures: GraphiteJobConsoleData["readout"] }) {
  return (
    <div className="-mx-4 mt-[22px] flex border-y py-0.5">
      {figures.map((figure, index) => (
        <div
          key={figure.label}
          className={cn("min-w-0 flex-1 px-4", index > 0 && "border-l")}
        >
          <div className="font-mono text-[22px] font-medium leading-none">
            {figure.value}
          </div>
          <div className="mt-2 whitespace-nowrap text-[10.5px] text-muted-foreground">
            {figure.label}
          </div>
        </div>
      ))}
    </div>
  )
}

/** Proportional bar plus a legend — the pipeline at a glance, without a board. */
function StageBar({ stages }: { stages: StageCount[] }) {
  /*
    Each stage keeps a 2px sliver so an empty stage between filled ones still
    marks its place. With nobody in the pipeline there is nothing to mark, and
    the slivers drew as a stray tick — so an empty pipeline is one plain track.
  */
  const isEmpty = stages.every((stage) => stage.count === 0)
  return (
    <div className="mt-[22px]">
      {isEmpty ? (
        <div className="h-1 bg-border" aria-hidden />
      ) : (
        <div className="flex gap-1">
          {stages.map((stage) => (
            <div
              key={stage.name}
              className="h-1 min-w-[2px]"
              style={{ flex: stage.count, backgroundColor: stage.color }}
            />
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2.5">
        {stages.map((stage) => (
          <span
            key={stage.name}
            className="inline-flex items-baseline gap-[7px] whitespace-nowrap text-[11px] text-muted-foreground"
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: stage.color }}
            />
            {stage.name}
            <span className="font-mono text-[11.5px] font-medium text-foreground">
              {stage.count}
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

/*
  Anything that opens something reads as a link before it is hovered: an
  underline that is always there (and a pointer — buttons get the default
  cursor). Candidate names carry the app's link colour; a task title keeps its
  weight with a faint underline; the activity primitive, already teal mono,
  gets a dotted one. Hover firms each up.

  The teal underlines are stronger in light: a tint that reads on near-black
  washes out on white (brand-ink at 40% is 1.8:1 there). Light keeps them at
  3:1 or better; a dotted line needs full strength because its dots are sparse.
*/
const LINK =
  "min-w-0 cursor-pointer truncate text-left underline decoration-1 underline-offset-[3px] transition-colors"
// `truncate` clips overflow, and at the title's tight leading the underline sat
// just outside the box; the padding keeps it inside.
const LINK_TITLE = `${LINK} pb-[3px] decoration-foreground/45 hover:text-brand-ink hover:decoration-brand-ink`
const LINK_NAME = `${LINK} text-brand-ink decoration-brand-ink/70 dark:decoration-brand-ink/40 hover:decoration-brand-ink dark:hover:decoration-brand-ink`
const LINK_PRIMITIVE = `${LINK} text-brand-ink decoration-dotted decoration-brand-ink dark:decoration-brand-ink/60 hover:decoration-solid`

/**
 * The row's own record opens from its title — the task, or what the activity
 * points at — and the candidate it names opens from their name. Two targets
 * rather than one clickable row, because a row about Maya holds both a task
 * and a person, and the reader should get the one they clicked.
 */
function CandidateName({
  name,
  candidateId,
  onOpenCandidate,
  className,
}: {
  name: string
  candidateId?: string
  onOpenCandidate?: (candidateId: string) => void
  className?: string
}) {
  if (!candidateId || !onOpenCandidate) return <span className={className}>{name}</span>
  return (
    <button type="button" onClick={() => onOpenCandidate(candidateId)} className={cn(LINK_NAME, className)}>
      {name}
    </button>
  )
}

function TaskRow({
  task,
  onOpenTask,
  onOpenCandidate,
}: {
  task: ConsoleTask
  onOpenTask?: (taskId: string) => void
  onOpenCandidate?: (candidateId: string) => void
}) {
  const tone = TASK_TONE[task.tone ?? "upcoming"]
  // The meta line leads with the candidate's name when it names one; only that
  // part links to them, the state after it stays text.
  const rest =
    task.candidateName && task.meta.startsWith(task.candidateName)
      ? task.meta.slice(task.candidateName.length)
      : null
  /*
    On a phone the four columns left the title a hundred pixels and the
    candidate's name an ellipsis — too small to read or tap. A narrow console
    stacks the meta under the title instead; from @xl there is room for both.
  */
  return (
    <div className="grid grid-cols-[8px_minmax(0,1fr)_64px] items-center gap-x-3.5 gap-y-1 border-b py-[11px] @xl:grid-cols-[8px_minmax(0,1.1fr)_minmax(0,1fr)_64px] @xl:gap-y-0">
      <span className={cn("h-[5px] w-[5px] rounded-full", tone.dot)} />
      {onOpenTask ? (
        <button
          type="button"
          onClick={() => onOpenTask(task.id)}
          className={cn(LINK_TITLE, "text-[12.5px] font-medium leading-[1.3]")}
        >
          {task.title}
        </button>
      ) : (
        <span className="truncate text-[12.5px] font-medium leading-[1.3]">{task.title}</span>
      )}
      <span className="col-start-2 row-start-2 flex min-w-0 text-xs leading-[1.3] text-muted-foreground @xl:col-start-auto @xl:row-start-auto">
        {rest !== null && task.candidateName ? (
          <>
            <CandidateName
              name={task.candidateName}
              candidateId={task.candidateId}
              onOpenCandidate={onOpenCandidate}
              className="shrink truncate"
            />
            <span className="shrink-0 whitespace-pre">{rest}</span>
          </>
        ) : (
          <span className="truncate">{task.meta}</span>
        )}
      </span>
      <span className={cn("text-right font-mono text-[11px]", tone.due)}>
        {task.due}
      </span>
    </div>
  )
}

/**
 * One entry in the job's activity: what happened (the primitive, in mono), to
 * whom, and when — plus, for a comment, what it said.
 *
 * The desktop row is three columns: time, primitive, candidate. On a phone the
 * fixed time and primitive columns left the name an ellipsis, so the narrow
 * row reads "primitive · name" with the time pushed to the right edge. A
 * comment's text sits under the row as a quote, two lines at most, and opens
 * the comment like the primitive does — the quote is usually what someone
 * wants to read in full.
 */
function ActivityRow({
  entry,
  onOpenActivity,
  onOpenCandidate,
}: {
  entry: ActivityEntry
  onOpenActivity?: (target: ActivityTarget) => void
  onOpenCandidate?: (candidateId: string) => void
}) {
  const target = entry.target
  const open = target && onOpenActivity ? () => onOpenActivity(target) : undefined
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1.5 border-b py-2 @xl:grid-cols-[76px_104px_minmax(0,1fr)] @xl:gap-x-3.5">
      {/* The primitive Robin ran — machine output, and the job's receipt trail. */}
      {open ? (
        <button
          type="button"
          onClick={open}
          className={cn(LINK_PRIMITIVE, "font-mono text-[10.5px] leading-[1.5] @xl:col-start-2 @xl:row-start-1")}
        >
          {entry.primitive}
        </button>
      ) : (
        <span className="font-mono text-[10.5px] leading-[1.5] text-brand-ink @xl:col-start-2 @xl:row-start-1">
          {entry.primitive}
        </span>
      )}
      <CandidateName
        name={entry.record}
        candidateId={entry.candidateId}
        onOpenCandidate={onOpenCandidate}
        className="truncate text-xs leading-[1.5] @xl:col-start-3 @xl:row-start-1"
      />
      <span className="text-right font-mono text-[10.5px] leading-[1.5] text-muted-foreground @xl:col-start-1 @xl:row-start-1 @xl:text-left">
        {entry.time}
      </span>
      {entry.detail &&
        (open ? (
          <button
            type="button"
            onClick={open}
            className={cn(QUOTE, "cursor-pointer transition-colors hover:border-brand-ink/50 hover:text-foreground")}
          >
            {entry.detail}
          </button>
        ) : (
          <p className={QUOTE}>{entry.detail}</p>
        ))}
    </div>
  )
}

// Full width on a phone; under the primitive and name on desktop, clear of
// the time column.
const QUOTE =
  "col-span-3 line-clamp-2 border-l-2 border-border pl-2.5 text-left text-xs leading-[1.5] text-muted-foreground @xl:col-span-2 @xl:col-start-2"

function KeyValueSheet({
  label,
  rows,
}: {
  label: string
  rows: { key: string; value: string }[]
}) {
  return (
    <div>
      <div className={cn(LABEL, "mb-1.5")}>{label}</div>
      {rows.map((row) => (
        <div key={row.key} className="flex gap-3 border-b py-[7px]">
          <span className="w-[88px] shrink-0 font-mono text-[10.5px] leading-[1.5] text-muted-foreground">
            {row.key}
          </span>
          <span className="min-w-0 flex-1 text-xs leading-[1.5]">{row.value}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * A section whose request has not landed, or failed, says so in one line
 * instead of drawing an empty list — an empty list reads as "this job has
 * none", which is a claim the console cannot make yet.
 */
function SectionNotice({ state, noun }: { state: Exclude<SectionState, "ready">; noun: string }) {
  return (
    <p className="border-b py-[11px] text-xs text-muted-foreground">
      {state === "loading" ? `Reading ${noun}…` : `${noun[0].toUpperCase()}${noun.slice(1)} cannot be read right now.`}
    </p>
  )
}

const ALL_READY: JobConsoleSections = {
  tasks: "ready",
  activity: "ready",
  hiringManagers: "ready",
  linkedSearches: "ready",
}

/*
  Activity is context under the work, not the work: a handful of the newest
  entries, and more on request, a handful at a time — a long ledger at full
  length pushed everything under it off the page.
*/
const ACTIVITY_STEP = 5

export function GraphiteJobConsole({
  data,
  sections = ALL_READY,
  onAddTask,
  onOpenSearch,
  onOpenTask,
  onOpenActivity,
  onOpenCandidate,
  onManageHiringManagers,
}: {
  data: GraphiteJobConsoleData
  /** Readiness of the sections fed by their own requests; all ready if omitted. */
  sections?: JobConsoleSections
  /**
   * Opens the task composer against this job. Optional so the console can be
   * rendered somewhere that has no composer — where it then shows no control
   * rather than a dead one.
   */
  onAddTask?: () => void
  /**
   * Opens the search this job is fed by, by its query hash — the key its
   * results route takes. Same reasoning as `onAddTask`.
   */
  onOpenSearch?: (hash: string) => void
  /** Opens a task in the editor. Rows stay text without it. */
  onOpenTask?: (taskId: string) => void
  /** Opens what an activity entry points at. */
  onOpenActivity?: (target: ActivityTarget) => void
  /** Opens a candidate's profile from their name in a task or activity row. */
  onOpenCandidate?: (candidateId: string) => void
  /** Opens the dialog that adds and removes the job's hiring managers. */
  onManageHiringManagers?: () => void
}) {
  // Open by default: the description is what the job is, and a recruiter
  // reading Today should not have to ask for it.
  const [descriptionOpen, setDescriptionOpen] = useState(true)
  // Closed by default: the ledger is context for the work above it, there
  // when asked for rather than in the way.
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityShown, setActivityShown] = useState(ACTIVITY_STEP)

  // The spec and linked searches: the static reference the rail holds.
  const hasReference =
    data.spec.length > 0 ||
    sections.linkedSearches !== "ready" ||
    data.linkedSearches.length > 0

  const activity = data.activity.slice(0, activityShown)
  const hidden = data.activity.length - activity.length

  return (
    // One surface, hairlines inside it. No cards.
    <div className="@container h-full overflow-y-auto bg-background p-3.5">
      <div className="rounded-[10px] border bg-card px-6 pb-6 pt-5">
        {data.alert && (
          <div className="mt-4 flex items-center gap-2.5 border-l-2 border-[#FB8A3C] bg-[#FB8A3C]/[0.06] px-3.5 py-2.5">
            <Clock className="h-4 w-4 shrink-0 text-[#FB8A3C]" />
            <span className="text-[12.5px]">{data.alert}</span>
            {/*
              The alert names the thing to do, so the control beside it has to
              do it. It had no handler at all: the one recommended action on a
              job that has gone quiet was a word that did nothing when pressed.
            */}
            {onAddTask && (
              <button
                type="button"
                onClick={onAddTask}
                className="ml-auto whitespace-nowrap text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
              >
                Add task
              </button>
            )}
          </div>
        )}

        <ReadoutStrip figures={data.readout} />
        <StageBar stages={data.stages} />

        {/*
          Two columns on desktop — the working half, and the static half in a
          right rail — and one on a phone, where the rail's sections come
          after the work. The two orders differ: on a phone the activity sits
          under the job's spec and hiring managers close the page, so each
          column is `display: contents` there and its sections are ordered
          directly in the one flex column. From @4xl the columns are real
          boxes and the order classes no longer apply.
        */}
        <div className="mt-[26px] flex flex-col gap-[30px] @4xl:flex-row">
          <div className="contents @4xl:block @4xl:min-w-0 @4xl:flex-1">
            <div className="order-1">
              <div className={cn(LABEL, "mb-1.5")}>
                Open tasks{sections.tasks === "ready" && ` · ${data.tasks.length}`}
              </div>
              {sections.tasks === "ready" ? (
                data.tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onOpenTask={onOpenTask}
                    onOpenCandidate={onOpenCandidate}
                  />
                ))
              ) : (
                <SectionNotice state={sections.tasks} noun="tasks" />
              )}

              {/* Description collapses to a disclosure — it is reference, not
                  work. A heading with nothing under it is not a section, so a
                  job without a description, requirements or spec draws none. */}
              {data.description.trim() && (
                <div className="mt-6">
                  <button
                    type="button"
                    aria-expanded={descriptionOpen}
                    onClick={() => setDescriptionOpen((open) => !open)}
                    className="flex w-full items-center gap-2 border-b py-2 text-left"
                  >
                    <span className={LABEL}>Job description</span>
                    <ChevronDown
                      className={cn(
                        "ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform",
                        descriptionOpen && "rotate-180",
                      )}
                    />
                  </button>
                  {descriptionOpen && (
                    <p className="text-pretty pt-3 text-[12.5px] leading-[1.6]">
                      {data.description}
                    </p>
                  )}
                </div>
              )}

              {data.requirements.length > 0 && (
                <div className="mt-6">
                  <div className={cn(LABEL, "mb-1.5")}>Requirements</div>
                  {data.requirements.map((requirement, index) => (
                    <div key={requirement} className="flex gap-3 py-1.5">
                      <span className="shrink-0 font-mono text-[10.5px] leading-[1.6] text-muted-foreground">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[12.5px] leading-[1.6]">{requirement}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="order-3 @4xl:mt-6">
              <button
                type="button"
                aria-expanded={activityOpen}
                onClick={() => setActivityOpen((open) => !open)}
                className="flex w-full items-center gap-2 border-b py-2 text-left"
              >
                {/*
                  The header carries the section's state, so a collapsed
                  section never hides that the activity is still loading or
                  could not be read — closed and failed must not look alike.
                */}
                <span className={LABEL}>
                  Activity
                  {sections.activity === "ready" && ` · ${data.activity.length}`}
                  {sections.activity === "loading" && " · loading"}
                </span>
                {sections.activity === "error" && (
                  <span className="text-[11px] text-destructive">Could not be read</span>
                )}
                <ChevronDown
                  className={cn(
                    "ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform",
                    activityOpen && "rotate-180",
                  )}
                />
              </button>
              {activityOpen && (sections.activity === "ready" ? (
                activity.map((entry) => (
                  <ActivityRow
                    key={entry.id}
                    entry={entry}
                    onOpenActivity={onOpenActivity}
                    onOpenCandidate={onOpenCandidate}
                  />
                ))
              ) : (
                <SectionNotice state={sections.activity} noun="activity" />
              ))}
              {activityOpen && sections.activity === "ready" && data.activity.length > ACTIVITY_STEP && (
                <div className="mt-2.5 flex gap-4">
                  {hidden > 0 && (
                    <button
                      type="button"
                      onClick={() => setActivityShown((shown) => shown + ACTIVITY_STEP)}
                      className="text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
                    >
                      Show {Math.min(hidden, ACTIVITY_STEP)} more
                    </button>
                  )}
                  {activityShown > ACTIVITY_STEP && (
                    <button
                      type="button"
                      onClick={() => setActivityShown(ACTIVITY_STEP)}
                      className="text-[11.5px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Show fewer
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="contents @4xl:block @4xl:w-[290px] @4xl:shrink-0 @4xl:space-y-6">
            {/* Drawn only with something in it: an empty box still takes the column's gap. */}
            {hasReference && (
              <div className="order-2 space-y-6">
                {data.spec.length > 0 && <KeyValueSheet label="Spec" rows={data.spec} />}

                {/*
                  A heading with nothing under it is not a section. This one was
                  hard-coded empty, so every job in the product carried the words
                  "Linked searches" above a blank — and the rows, when they finally
                  had something to draw, hovered like links and went nowhere.
                */}
                {/* Only a job that names a search is ever not "ready" here. */}
                {sections.linkedSearches !== "ready" && (
                  <div>
                    <div className={cn(LABEL, "mb-1.5")}>Linked searches</div>
                    <SectionNotice state={sections.linkedSearches} noun="the linked search" />
                  </div>
                )}
                {sections.linkedSearches === "ready" && data.linkedSearches.length > 0 && (
                  <div>
                    <div className={cn(LABEL, "mb-1.5")}>Linked searches</div>
                    {data.linkedSearches.map((search) => (
                      <button
                        key={search.id}
                        type="button"
                        // No hash, nowhere to go — and a row that cannot move
                        // should not hover as though it can.
                        disabled={!search.hash}
                        onClick={() => search.hash && onOpenSearch?.(search.hash)}
                        className="block w-full border-b py-2 text-left transition-colors enabled:hover:bg-foreground/[0.03] disabled:cursor-default"
                      >
                        <span className="block truncate text-xs">{search.name}</span>
                        <span className="mt-0.5 block truncate font-mono text-[10.5px] text-muted-foreground">
                          {search.meta}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="order-4">
              {/*
                The heading carries the way to change the list: pick one of the
                organization's contacts, or create one, in the dialog it opens.
                The redesign drew this list read-only and left that dialog
                rendered nowhere, so a job's hiring managers could be seen but
                not set.
              */}
              <div className="mb-1.5 flex items-baseline gap-2">
                <span className={LABEL}>Hiring managers</span>
                {onManageHiringManagers && sections.hiringManagers === "ready" && data.hiringManagers.length > 0 && (
                  <button
                    type="button"
                    onClick={onManageHiringManagers}
                    className="ml-auto text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
                  >
                    Manage
                  </button>
                )}
              </div>
              {sections.hiringManagers !== "ready" && (
                <SectionNotice state={sections.hiringManagers} noun="hiring managers" />
              )}
              {sections.hiringManagers === "ready" && data.hiringManagers.length === 0 && (
                <div className="py-2">
                  <p className="text-xs text-muted-foreground">No hiring managers on this job yet.</p>
                  {onManageHiringManagers && (
                    <button
                      type="button"
                      onClick={onManageHiringManagers}
                      className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-brand-ink transition-opacity hover:opacity-80"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add hiring manager
                    </button>
                  )}
                </div>
              )}
              {sections.hiringManagers === "ready" && data.hiringManagers.map((manager) => (
                <div key={manager.id} className="flex items-center gap-2.5 py-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-[10px] font-medium text-brand-ink">
                    {manager.initials}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-xs">{manager.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {manager.role}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The pinned job header, wide from canvas 4a and narrow from the mobile job
 * overview.
 *
 * The narrow bar used to follow 8e: the tab name over the job title, and the
 * tab strip folded into an overflow menu. That hid the one thing that tells you
 * where you are on a job and how to get anywhere else — and it is not how the
 * app's other records behave (`RecordTabs` scrolls sideways rather than going
 * away). The mobile overview draws the job's own title, its company and status,
 * and the four tabs underneath; the pills and Edit job fold into the overflow.
 *
 * The tab strip is one copy, rendered at every width, so the two layouts can't
 * drift apart.
 */
export function GraphiteJobHeader({
  data,
  jobId,
  activeTab,
  onTabChange,
  tabs,
}: {
  /*
    Only what the bar renders. It used to take the whole console object, which
    tied the job's name and status to four endpoints it never reads — so a
    pending pipeline request could hold up the title of the job you just opened.
  */
  data: Pick<
    GraphiteJobConsoleData,
    "title" | "subtitle" | "company" | "status" | "priority"
  >
  /** The job Edit Job edits. */
  jobId: string
  activeTab: string
  onTabChange: (tab: string) => void
  tabs: { id: string; label: string }[]
}) {
  // 18a: the view's own action belongs in the bar, not in a header drawn
  // inside the view — and a view doing something of its own (18b's composer)
  // may rename the bar while it does.
  const { action, title } = useJobHeaderAction()
  const [editOpen, setEditOpen] = useState(false)

  return (
    <header className="@container shrink-0 border-b bg-chrome">
      {/* Narrow, per the mobile job overview. */}
      <div className="flex min-h-[56px] items-center gap-2.5 px-4 @3xl:hidden">
        <Link
          to="/jobs"
          aria-label="Back to jobs"
          className="-ml-3 flex h-11 w-11 flex-none items-center justify-center text-muted-foreground"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>

        <div className="min-w-0 py-2">
          <div className="truncate text-[15px] font-medium leading-[1.25]">
            {title ?? data.title}
          </div>
          <div className="mt-[3px] truncate font-mono text-[10.5px] leading-[1.2] text-muted-foreground">
            {[data.company, data.status].filter(Boolean).join(" · ")}
          </div>
        </div>

        <div className="ml-auto flex flex-none items-center">
          {action}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Job actions"
                className="-mr-3 flex h-11 w-11 flex-none items-center justify-center text-muted-foreground"
              >
                <MoreHorizontal className="h-[18px] w-[18px]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="font-mono text-[10.5px] font-normal text-muted-foreground">
                {[data.status, data.priority].filter(Boolean).join(" · ")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setEditOpen(true)} className="text-[12.5px]">
                Edit job
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Outside the menu: its content unmounts when the menu closes. */}
          <JobEditDialog jobId={jobId} open={editOpen} onOpenChange={setEditOpen} />
        </div>
      </div>

      {/* Wide, per 4a. */}
      <div className="hidden px-6 pt-5 @3xl:block">
        <div className="flex flex-wrap items-start gap-3.5">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold leading-[1.1] tracking-[-0.025em]">
              {data.title}
            </h1>
            <p className="mt-[7px] text-[12.5px] leading-[1.5] text-muted-foreground">
              {data.subtitle}
            </p>
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            {action}
            <span className="whitespace-nowrap rounded-full border border-primary/35 bg-primary/[0.08] px-[9px] py-[5px] text-[11.5px] text-brand-ink">
              {data.status}
            </span>
            <span className="whitespace-nowrap rounded-full border px-[9px] py-[5px] text-[11.5px] text-muted-foreground">
              {data.priority}
            </span>
            {/*
              4a draws this and it shipped with no handler. The dialog that
              edits a job already exists and takes its own trigger, so it just
              had to be handed one.
            */}
            <JobEditDialog
              jobId={jobId}
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center whitespace-nowrap text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80"
                >
                  Edit job
                </button>
              }
            />
            {/*
              4a draws an Add Candidate here, but it never had a handler, and
              canvas 19 gives the pipeline a real one. Two identical buttons
              where only one works is worse than one button, so the dead copy
              goes and the working one keeps its place on the pipeline.
            */}
          </div>
        </div>
      </div>

      {/* Every width. Scrolls sideways on a narrow screen rather than going away. */}
      <div
        role="tablist"
        aria-label="Job views"
        className="flex gap-[22px] overflow-x-auto px-4 [scrollbar-width:none] @3xl:mt-4 @3xl:px-6"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeTab}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              "-mb-px flex-none whitespace-nowrap border-b-2 px-0.5 py-2.5 text-[12.5px] transition-colors",
              tab.id === activeTab
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </header>
  )
}
